import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  BookOpen, Sparkles, Check, X, Star, Crown, Feather, Zap, 
  PenTool, Users, FileText, Mic, Download, Shield, Infinity,
  ChevronRight, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import ShweebumCat from "../components/ShweebumCat";
import { useAuth } from "../components/Auth/AuthProvider";
import { API } from "../lib/api";

const PricingPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const plans = [
    {
      id: "free",
      name: "Freebum",
      tagline: "Your Free Writing Playground",
      price: 0,
      icon: Feather,
      color: "cyan",
      gradient: "from-cyan-500 to-teal-500",
      bgGradient: "from-cyan-950/40 to-teal-950/20",
      borderColor: "border-cyan-500/50",
      glowColor: "shadow-cyan-500/30",
      description: "Powerful free tools to plan, write, and polish your story",
      highlights: [
        { icon: PenTool, text: "Full Story Planning Studio" },
        { icon: Users, text: "Character & World Builder" },
        { icon: Sparkles, text: "Writing Polish Tools" },
      ],
      features: [
        "Story Builder Wizard (outlines your book)",
        "Smart Upload (1 file at a time)",
        "Unlimited story planning & outlining",
        "Unlimited free writing (write as much as YOU want!)",
        "Character Creator with full detail",
        "World-building tools",
        "Story Doctor analysis (find plot holes)",
        "Writing Polish Tools (Show Don't Tell, Sensory, Humanizer)",
        "Manuscript Analysis (Readability + Consistency checks)",
        "Back Cover Blurb Generator",
        "10 assisted chapter generations",
        "2 book projects, up to 20 chapters each",
        "Export to TXT & EPUB formats",
        "Quick Voice Reader (browser)",
      ],
      limitations: [
        { text: "Shweebum Spark Intelligence", tooltip: "Good quality — upgrade to Nova for richer prose" },
        { text: "No Full Book Generation", tooltip: "Upgrade to write entire books with one click" },
        { text: "No Premium Voice Reader", tooltip: "Hear your story in expressive voices" },
        { text: "No Book Cover Design", tooltip: "Generate professional book covers" },
      ],
      cta: "Start Free",
      ctaVariant: "outline",
    },
    {
      id: "storyteller",
      name: "Shweebum Plus",
      tagline: "For Serious Storytellers",
      price: 10,
      icon: Star,
      color: "cyan",
      gradient: "from-cyan-500 to-blue-500",
      bgGradient: "from-slate-800/50 to-slate-700/40",
      borderColor: "border-cyan-500/60",
      glowColor: "shadow-cyan-500/30",
      popular: true,
      description: "15 AI chapters/month — write with Shweebum Nova intelligence",
      highlights: [
        { icon: Sparkles, text: "Shweebum Nova Intelligence" },
        { icon: BookOpen, text: "15 chapters/month" },
        { icon: Mic, text: "Premium Voice Reader" },
      ],
      features: [
        "Everything in Freebum, plus:",
        "Shweebum Nova (advanced writing intelligence)",
        "15 AI-generated chapters per month",
        "Up to 1 full book generation per month",
        "10 book projects, 30 chapters each",
        "Smart Upload (5 files at once)",
        "Premium Voice Reader (10 chapters/month)",
        "Book Cover Design (5 covers/month)",
        "Story Doctor + Fix Suggestions",
        "Export to PDF, DOCX, TXT, EPUB",
        "KDP Publishing Validator",
        "Email support",
      ],
      limitations: [
        { text: "No Continue My Style", tooltip: "Shweebum learns your unique writing voice" },
      ],
      cta: "Start Writing",
      ctaVariant: "default",
      savings: "Most Popular",
    },
    {
      id: "author",
      name: "Shweebum Premium",
      tagline: "Professional Creative Power",
      price: 20,
      icon: Crown,
      color: "violet",
      gradient: "from-violet-600 to-purple-600",
      bgGradient: "from-violet-950/50 to-purple-950/30",
      borderColor: "border-violet-500/60",
      glowColor: "shadow-violet-500/30",
      description: "60 AI chapters/month — write entire books with every premium feature",
      highlights: [
        { icon: Zap, text: "60 chapters/month" },
        { icon: Zap, text: "3 full books/month" },
        { icon: Crown, text: "Shweebum Learns Your Voice" },
      ],
      features: [
        "Everything in Plus, plus:",
        "60 AI-generated chapters per month",
        "Up to 3 full book generations per month",
        "Unlimited book projects & chapters",
        "Full Book Generation (entire book in one click!)",
        "3-part chapters (longer, richer writing)",
        "Unlimited Premium Voice Reader",
        "Unlimited Book Cover Design",
        "Continue My Style (Shweebum learns YOUR voice)",
        "Story Doctor + One-Click Fix All",
        "Smart Refine & Auto-Polish",
        "Re-Sync Chapter Outline (Verbatim Mode)",
        "Chapter Transition Awareness",
        "All export formats (PDF, EPUB, DOCX, TXT)",
        "Priority support",
        "Early access to new features",
      ],
      limitations: [],
      cta: "Go Premium",
      ctaVariant: "default",
      savings: "Best Value",
    }
  ];

  // Feature comparison for the table
  const comparisonFeatures = [
    { name: "Story Planning & Outlining", free: true, plus: true, premium: true, category: "Planning" },
    { name: "Character Creator", free: true, plus: true, premium: true, category: "Planning" },
    { name: "World Builder", free: true, plus: true, premium: true, category: "Planning" },
    { name: "Chapter Planning (3 modes)", free: true, plus: true, premium: true, category: "Planning" },
    { name: "Story Doctor Analysis", free: true, plus: true, premium: true, category: "Planning" },
    { name: "Writing Polish Tools", free: true, plus: true, premium: true, category: "Tools" },
    { name: "Manuscript Analysis (Readability)", free: true, plus: true, premium: true, category: "Tools" },
    { name: "Consistency Linter", free: true, plus: true, premium: true, category: "Tools" },
    { name: "Back Cover Blurb Generator", free: true, plus: true, premium: true, category: "Tools" },
    { name: "Shweebum Intelligence", free: "Spark", plus: "Nova", premium: "Nova", category: "AI" },
    { name: "Free Writing (manual)", free: "Unlimited", plus: "Unlimited", premium: "Unlimited", category: "Writing" },
    { name: "Assisted Chapter Writing", free: "10 total", plus: "15/month", premium: "60/month", category: "Writing" },
    { name: "Book Projects", free: "2", plus: "10", premium: "Unlimited", category: "Writing" },
    { name: "Chapters per Book", free: "20", plus: "30", premium: "Unlimited", category: "Writing" },
    { name: "Smart Upload Files", free: "1", plus: "5", premium: "Unlimited", category: "Import" },
    { name: "Book Cover Design", free: false, plus: "5/month", premium: "Unlimited", category: "Design" },
    { name: "Full Book Generation", free: false, plus: "1/month", premium: "3/month", category: "Writing" },
    { name: "Chapter Transition Awareness", free: false, plus: false, premium: true, category: "Writing" },
    { name: "Premium Voice Reader", free: false, plus: "10 chapters/mo", premium: "Unlimited", category: "Audio" },
    { name: "Continue My Style", free: false, plus: false, premium: true, category: "AI" },
    { name: "Re-Sync Verbatim Outline", free: false, plus: false, premium: true, category: "Tools" },
    { name: "Export TXT", free: true, plus: true, premium: true, category: "Export" },
    { name: "Export EPUB", free: true, plus: true, premium: true, category: "Export" },
    { name: "Export PDF, DOCX", free: false, plus: true, premium: true, category: "Export" },
    { name: "KDP Publishing Validator", free: false, plus: true, premium: true, category: "Export" },
    { name: "Story Doctor Fix All", free: false, plus: true, premium: true, category: "Tools" },
    { name: "Priority Support", free: false, plus: false, premium: true, category: "Support" },
  ];

  const handleUpgrade = async (planId) => {
    if (planId === "free") {
      if (!user) {
        navigate("/");
        toast.info("Create a free account to get started!");
      }
      return;
    }
    
    if (!user) {
      toast.error("Please log in to upgrade");
      navigate("/");
      return;
    }

    setLoading(planId);
    try {
      const res = await axios.post(`${API}/checkout/create?user_id=${user.id}`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not create checkout session");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(err.response?.data?.detail || "Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const getUserPlan = () => {
    if (!user) return null;
    return user.subscription || "free";
  };

  const currentPlan = getUserPlan();

  const renderFeatureValue = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-emerald-400" />;
    if (value === false) return <X className="w-5 h-5 text-slate-500" />;
    return <span className="text-sm font-medium text-slate-200">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#060A12] via-[#0A1628] to-[#060A12]">
      {/* Header */}
      <header className="border-b border-cyan-500/20 bg-[#0A0E17]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity group">
              {/* Magical Book Icon */}
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400/20 via-cyan-400/10 to-violet-500/20 rounded-lg flex items-center justify-center border border-cyan-500/20 shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-all">
                  <BookOpen className="w-5 h-5 text-cyan-400" />
                  <Sparkles className="w-2.5 h-2.5 text-cyan-400 absolute -top-0.5 -right-0.5 animate-pulse" />
                </div>
              </div>
              {/* Brand Name */}
              <div className="flex flex-col">
                <span className="font-literary text-xl bg-gradient-to-r from-cyan-300 via-cyan-200 to-cyan-300 bg-clip-text text-transparent font-semibold tracking-wide">
                  Shweebum
                </span>
                <span className="text-[9px] text-slate-500 -mt-0.5 tracking-widest uppercase hidden sm:block">Your Creative Partner</span>
              </div>
            </Link>
            
            <nav className="flex items-center gap-4">
              <Link to="/" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">Home</Link>
              {user ? (
                <Link to="/dashboard">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black font-semibold">
                    Get Started
                  </Button>
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border-cyan-500/20 px-4 py-1">
            Simple, Transparent Pricing
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Choose Your <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Writing Journey</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Start free, upgrade when you're ready. No hidden fees. Cancel anytime with one click — your content is always yours.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-20 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            const isCurrentPlan = currentPlan === plan.id;
            const isPlanUpgrade = currentPlan === "free" || 
              (currentPlan === "storyteller" && plan.id === "author");
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className={`bg-gradient-to-r ${plan.gradient} text-white border-0 px-4 py-1 shadow-lg ${plan.glowColor}`}>
                      {plan.savings}
                    </Badge>
                  </div>
                )}
                
                <Card className={`
                  relative overflow-hidden h-full
                  bg-gradient-to-b ${plan.bgGradient} 
                  ${plan.borderColor} border-2
                  hover:border-opacity-100 transition-all duration-300
                  ${plan.popular ? `shadow-xl ${plan.glowColor}` : 'shadow-lg'}
                `}>
                  {/* Glow effect */}
                  <div className={`absolute inset-0 bg-gradient-to-b ${plan.gradient} opacity-5`} />
                  
                  <CardHeader className="relative pb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${plan.gradient}`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white">{plan.name}</CardTitle>
                        <p className="text-xs text-slate-400">{plan.tagline}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-baseline gap-1 mt-4">
                      <span className="text-4xl font-bold text-white">${plan.price}</span>
                      <span className="text-slate-400">/month</span>
                    </div>
                    
                    <CardDescription className="text-slate-300 mt-2">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="relative space-y-6">
                    {/* Highlights */}
                    <div className="flex flex-wrap gap-2">
                      {plan.highlights.map((highlight, i) => {
                        const HIcon = highlight.icon;
                        return (
                          <Badge 
                            key={i} 
                            variant="outline" 
                            className={`${plan.borderColor} text-slate-300 bg-slate-900/50`}
                          >
                            <HIcon className="w-3 h-3 mr-1" />
                            {highlight.text}
                          </Badge>
                        );
                      })}
                    </div>

                    {/* Features List */}
                    <ul className="space-y-2">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 text-${plan.color}-400`} />
                          <span className="text-slate-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Limitations */}
                    {plan.limitations.length > 0 && (
                      <ul className="space-y-2 pt-2 border-t border-slate-700">
                        {plan.limitations.map((limitation, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                            <span className="text-slate-400">{limitation.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* CTA Button */}
                    <Button
                      className={`w-full mt-4 font-semibold ${
                        plan.id === "free" 
                          ? "bg-slate-800 hover:bg-slate-700 text-white border border-slate-600"
                          : `bg-gradient-to-r ${plan.gradient} hover:opacity-90 text-white shadow-lg`
                      }`}
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={loading === plan.id || isCurrentPlan}
                    >
                      {loading === plan.id ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </span>
                      ) : isCurrentPlan ? (
                        <span className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Current Plan
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {plan.cta}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-5xl mx-auto mb-20"
        >
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Compare All Features
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium">Feature</th>
                  <th className="text-center py-4 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <Feather className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-400 font-semibold">Freebum</span>
                      <span className="text-slate-400 text-xs">$0/mo</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-4 bg-cyan-500/5">
                    <div className="flex flex-col items-center gap-1">
                      <Star className="w-5 h-5 text-cyan-400" />
                      <span className="text-cyan-400 font-semibold">Plus</span>
                      <span className="text-slate-400 text-xs">$10/mo</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <Crown className="w-5 h-5 text-violet-400" />
                      <span className="text-violet-400 font-semibold">Premium</span>
                      <span className="text-slate-400 text-xs">$20/mo</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, i) => (
                  <tr 
                    key={i} 
                    className={`border-b border-slate-700/50 ${i % 2 === 0 ? 'bg-slate-900/30' : ''}`}
                  >
                    <td className="py-3 px-4 text-slate-300 text-sm">{feature.name}</td>
                    <td className="py-3 px-4 text-center">{renderFeatureValue(feature.free)}</td>
                    <td className="py-3 px-4 text-center bg-cyan-500/5">{renderFeatureValue(feature.plus)}</td>
                    <td className="py-3 px-4 text-center">{renderFeatureValue(feature.premium)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: "What's the difference between Spark and Nova Intelligence?",
                a: "Shweebum Spark is fast and great for planning and basic generation. Shweebum Nova writes richer, more nuanced prose and follows your style instructions more precisely. Plus and Premium plans include Nova for all writing."
              },
              {
                q: "What happens if I hit my chapter limit?",
                a: "You can still access your existing projects and do all planning activities. You just can't generate new AI-assisted chapters until the next month or you upgrade."
              },
              {
                q: "Can I cancel anytime?",
                a: "Absolutely! No contracts, no hidden fees. Cancel with one click from the 'Cancel Plan' button on your Dashboard. Your existing projects and content will never be deleted — even on the free plan."
              },
              {
                q: "Can I write unlimited words for free?",
                a: "Yes! Free writing — where YOU type directly — has no limit on any plan. The chapter caps (10 on Freebum, 15/month on Plus, 60/month on Premium) only apply to AI-generated chapters. Write as much as you want, anytime!"
              },
              {
                q: "What is 'Continue My Style'?",
                a: "Premium exclusive! Upload samples of your writing, and Shweebum learns your unique voice — word choices, sentence rhythms, tone. Your assisted chapters will sound like YOU wrote them."
              },
              {
                q: "What is Full Book Generation?",
                a: "Click one button and Shweebum writes your entire book — all chapters, fully fleshed out — based on your outline. Available on Plus (1 book/month) and Premium (3 books/month)."
              },
            ].map((faq, i) => (
              <Card key={i} className="bg-[#0F1629]/80 border-slate-700/50 hover:border-cyan-500/30 transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-white">{faq.q}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400 text-sm">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16 pb-8"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            Ready to write your story?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button size="lg" variant="outline" className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10">
                Start Free
              </Button>
            </Link>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black font-semibold shadow-lg shadow-cyan-500/25"
              onClick={() => handleUpgrade("storyteller")}
              data-testid="bottom-cta-plus"
            >
              Get Shweebum Plus
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold shadow-lg shadow-violet-500/25"
              onClick={() => handleUpgrade("author")}
              data-testid="bottom-cta-premium"
            >
              Go Premium
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-cyan-500/20 py-8 mt-12 bg-[#0A0E17]">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Shweebum — You Inspire Designs LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
